import React, { useEffect, useCallback } from "react";
import { connect } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import FacilityDetail from "../../components/maintenance/FacilityDetail";
import {
  fetchFacility,
  fetchFacilitySuccess,
  fetchFacilityFailure,
  fetchFacilityTasks,
  fetchFacilityTasksSuccess,
  fetchFacilityTasksFailure,
  deleteFacility,
  deleteFacilitySuccess,
  deleteFacilityFailure,
  startScheduledTask,
  startScheduledTaskSuccess,
  startScheduledTaskFailure,
  completeScheduledTask,
  completeScheduledTaskSuccess,
  completeScheduledTaskFailure,
  clearCurrentFacility,
} from "../../store/maintenance/actions";

const FacilityDetailContainer = ({
  facility,
  tasks,
  loading,
  tasksLoading,
  error,
  fetchFacility,
  fetchFacilityTasks,
  deleteFacility,
  startScheduledTask,
  completeScheduledTask,
  clearCurrentFacility,
}) => {
  const { id } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    if (id) {
      fetchFacility(id);
      fetchFacilityTasks(id);
    }
    return () => {
      clearCurrentFacility();
    };
  }, [id, fetchFacility, fetchFacilityTasks, clearCurrentFacility]);

  const handleDelete = useCallback(
    async (facilityId) => {
      try {
        await deleteFacility(facilityId);
        navigate("/maintenance/facilities");
      } catch (err) {
        console.error("Error deleting facility:", err);
      }
    },
    [deleteFacility, navigate]
  );

  const handleStartTask = useCallback(
    async (taskId) => {
      await startScheduledTask(taskId);
      fetchFacilityTasks(id);
    },
    [startScheduledTask, fetchFacilityTasks, id]
  );

  const handleCompleteTask = useCallback(
    async (taskId) => {
      await completeScheduledTask(taskId, {});
      fetchFacilityTasks(id);
    },
    [completeScheduledTask, fetchFacilityTasks, id]
  );

  return (
    <FacilityDetail
      facility={facility}
      tasks={tasks}
      loading={loading}
      tasksLoading={tasksLoading}
      error={error}
      onDelete={handleDelete}
      onStartTask={handleStartTask}
      onCompleteTask={handleCompleteTask}
    />
  );
};

const mapStateToProps = (state) => ({
  facility: state.maintenance.facilities.current,
  tasks: state.maintenance.facilities.tasks,
  loading: state.maintenance.facilities.loading,
  tasksLoading: state.maintenance.scheduledTasks.loading,
  error: state.maintenance.facilities.error,
});

const mapDispatchToProps = (dispatch) => ({
  fetchFacility: (id) => {
    dispatch(fetchFacility(id)).payload.then(
      (response) => {
        dispatch(fetchFacilitySuccess(response.data));
      },
      (error) => {
        dispatch(fetchFacilityFailure(error.response?.data || error));
      }
    );
  },
  fetchFacilityTasks: (id) => {
    dispatch(fetchFacilityTasks(id)).payload.then(
      (response) => {
        dispatch(fetchFacilityTasksSuccess(response.data));
      },
      (error) => {
        dispatch(fetchFacilityTasksFailure(error.response?.data || error));
      }
    );
  },
  deleteFacility: (id) => {
    return dispatch(deleteFacility(id)).payload.then(
      () => {
        dispatch(deleteFacilitySuccess(id));
      },
      (error) => {
        dispatch(deleteFacilityFailure(error.response?.data || error));
        throw error;
      }
    );
  },
  startScheduledTask: (id) => {
    return dispatch(startScheduledTask(id)).payload.then(
      (response) => {
        dispatch(startScheduledTaskSuccess(response.data));
      },
      (error) => {
        dispatch(startScheduledTaskFailure(error.response?.data || error));
      }
    );
  },
  completeScheduledTask: (id, data) => {
    return dispatch(completeScheduledTask(id, data)).payload.then(
      (response) => {
        dispatch(completeScheduledTaskSuccess(response.data));
      },
      (error) => {
        dispatch(completeScheduledTaskFailure(error.response?.data || error));
      }
    );
  },
  clearCurrentFacility: () => dispatch(clearCurrentFacility()),
});

export default connect(mapStateToProps, mapDispatchToProps)(FacilityDetailContainer);
